import { Form, message } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ForgotPassword as ForgotPasswordApi,
  VerifyForgotOtp,
  ResetPassword,
} from "../../ApiService/Apis/Auth/Auth";
import { colors, MakeError, Makelabel } from "../../Utils/Index";
import CommonButton from "../../Components/CommonButton";
import CommonInput from "../../Components/CommonInput";

// Steps: "email" -> "otp" -> "reset" -> "done"
export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email");
  const [showLoading, setShowLoading] = useState(false);

  // Data carried forward between steps
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState("");

  // ── Step 1: send OTP to email ──
  const handleSendOtp = (val) => {
    setShowLoading(true);
    ForgotPasswordApi({ email: val.email })
      .then((res) => {
        if (res?.status === "success") {
          setEmail(val.email);
          message.success(res.message || "OTP sent successfully to your email!");
          setStep("otp");
        } else {
          message.error(res?.message || "Failed to send OTP");
        }
      })
      .catch((err) => {
        console.log(err);
        message.error(
          err?.response?.data?.message || "Failed to send OTP. Please try again."
        );
      })
      .finally(() => setShowLoading(false));
  };

  // ── Step 2: verify OTP, get resetToken ──
  const handleVerifyOtp = (val) => {
    setShowLoading(true);
    VerifyForgotOtp({ email, otp: val.otp })
      .then((res) => {
        if (res?.status === "success") {
          setResetToken(res?.data?.resetToken);
          message.success(res.message || "OTP verified successfully!");
          setStep("reset");
        } else {
          message.error(res?.message || "Invalid OTP");
        }
      })
      .catch((err) => {
        console.log(err);
        message.error(
          err?.response?.data?.message || "OTP verification failed. Please try again."
        );
      })
      .finally(() => setShowLoading(false));
  };

  // ── Step 3: set new password ──
  const handleResetPassword = (val) => {
    if (val.password !== val.confirmPassword) {
      message.error("Passwords do not match");
      return;
    }
    setShowLoading(true);
    ResetPassword({ email, resetToken, password: val.password })
      .then((res) => {
        if (res?.status === "success") {
          message.success(
            res.message || "Password reset successfully! Please login with your new password."
          );
          setStep("done");
          setTimeout(() => navigate("/"), 1500);
        } else {
          message.error(res?.message || "Failed to reset password");
        }
      })
      .catch((err) => {
        console.log(err);
        message.error(
          err?.response?.data?.message || "Failed to reset password. Please try again."
        );
      })
      .finally(() => setShowLoading(false));
  };

  // ── Resend OTP (re-uses step 1 API, keeps user on the OTP screen) ──
  const handleResendOtp = () => {
    if (!email) return;
    setShowLoading(true);
    ForgotPasswordApi({ email })
      .then((res) => {
        if (res?.status === "success") {
          message.success(res.message || "OTP resent successfully!");
        } else {
          message.error(res?.message || "Failed to resend OTP");
        }
      })
      .catch((err) => {
        console.log(err);
        message.error(err?.response?.data?.message || "Failed to resend OTP");
      })
      .finally(() => setShowLoading(false));
  };

  return (
    <div className=" ">
      <div className="flex justify-center signInbg text-black m-auto h-full items-center">
        <div className="bg-[#26272cb3] w-[400px] flex justify-center flex-col px-8 py-6 m-4 items-center">
          <div className="flex justify-center items-center text-dynmaicBg2">
            <p className="text-4xl font-quicksand font-semibold">Restro</p>
          </div>

          {/* ── STEP 1: Email ── */}
          {step === "email" && (
            <>
              <p className="text-dynmaicBg2 text-sm mt-3 mb-1 text-center">
                Enter your email to receive a password reset OTP
              </p>
              <Form
                onFinish={(val) => handleSendOtp(val)}
                className="w-full mt-2"
                labelCol={{ span: 24 }}
              >
                <Form.Item
                  rules={[
                    { required: true, message: MakeError("enter email") },
                  ]}
                  className="w-full m-1"
                  name={"email"}
                  label={Makelabel("Email")}
                >
                  <CommonInput placeholder={"Enter your email"} />
                </Form.Item>
                <Form.Item className="w-full ">
                  <CommonButton
                    loading={showLoading}
                    margintop={"10px"}
                    htmltype={"submit"}
                    color={colors.green}
                    text={"Send OTP"}
                  />
                </Form.Item>
              </Form>
              <p
                onClick={() => navigate("/")}
                className="text-dynmaicBg2 text-xs mt-2 cursor-pointer underline"
              >
                Back to login
              </p>
            </>
          )}

          {/* ── STEP 2: OTP ── */}
          {step === "otp" && (
            <>
              <p className="text-dynmaicBg2 text-sm mt-3 mb-1 text-center">
                Enter the OTP sent to <span className="font-semibold">{email}</span>
              </p>
              <Form
                onFinish={(val) => handleVerifyOtp(val)}
                className="w-full mt-2"
                labelCol={{ span: 24 }}
              >
                <Form.Item
                  rules={[{ required: true, message: MakeError("enter OTP") }]}
                  className="w-full m-1"
                  name={"otp"}
                  label={Makelabel("OTP")}
                >
                  <CommonInput placeholder={"Enter 6-digit OTP"} />
                </Form.Item>
                <Form.Item className="w-full ">
                  <CommonButton
                    loading={showLoading}
                    margintop={"10px"}
                    htmltype={"submit"}
                    color={colors.green}
                    text={"Verify OTP"}
                  />
                </Form.Item>
              </Form>
              <p
                onClick={handleResendOtp}
                className="text-dynmaicBg2 text-xs mt-2 cursor-pointer underline"
              >
                Resend OTP
              </p>
              <p
                onClick={() => setStep("email")}
                className="text-dynmaicBg2 text-xs mt-2 cursor-pointer underline"
              >
                Change email
              </p>
            </>
          )}

          {/* ── STEP 3: Reset password ── */}
          {step === "reset" && (
            <>
              <p className="text-dynmaicBg2 text-sm mt-3 mb-1 text-center">
                Set a new password for your account
              </p>
              <Form
                onFinish={(val) => handleResetPassword(val)}
                className="w-full mt-2"
                labelCol={{ span: 24 }}
              >
                <Form.Item
                  rules={[
                    { required: true, message: MakeError("enter new password") },
                    { min: 6, message: MakeError("password must be at least 6 characters") },
                  ]}
                  className="w-full m-1"
                  name={"password"}
                  label={Makelabel("New Password")}
                >
                  <CommonInput type="password" placeholder={"Enter new password"} />
                </Form.Item>
                <Form.Item
                  rules={[
                    { required: true, message: MakeError("confirm new password") },
                  ]}
                  className="w-full m-1"
                  name={"confirmPassword"}
                  label={Makelabel("Confirm Password")}
                >
                  <CommonInput type="password" placeholder={"Re-enter new password"} />
                </Form.Item>
                <Form.Item className="w-full ">
                  <CommonButton
                    loading={showLoading}
                    margintop={"10px"}
                    htmltype={"submit"}
                    color={colors.green}
                    text={"Reset Password"}
                  />
                </Form.Item>
              </Form>
            </>
          )}

          {/* ── STEP 4: Done ── */}
          {step === "done" && (
            <p className="text-dynmaicText text-sm mt-4 text-center">
              Password reset successful! Redirecting to login…
            </p>
          )}
        </div>
      </div>
    </div>
  );
}